import {
  PracticeTemplateDetail,
  ProgramBenchmarkName,
  ProgramTask,
  TaskDataTraitStatus,
} from '@cibo/core'
import { Dialog, Stack } from '@mui/material'
import { map, pipe, sort, uniq } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { useProgramEngagementContext } from '../../../../../context'
import { pickDetailValue } from '../../../models/TaskDataModel'
import { TemplateDeleteConfirmDialog } from './TemplateDeleteConfirmDialog'
import { TemplateEditorContext } from './TemplateEditorContext'
import { TemplateEditorMain } from './TemplateEditorMain'
import { TemplateEditorSidebar } from './TemplateEditorSidebar'
import { TemplateUnsavedGuard } from './TemplateUnsavedGuard'
import { useTemplateEditorState } from './useTemplateEditorState'

interface TemplateEditorModalProps {
  open: boolean
  onClose: () => void
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  initialTemplateId?: string
}
export const TemplateEditorModal = ({
  open,
  onClose,
  task,
  initialTemplateId,
}: TemplateEditorModalProps) => {
  const { engagement, program, fieldModels } = useProgramEngagementContext()
  const programId = engagement.data?.programId ?? ''
  const taskId = task.id
  const selectedField = fieldModels.data?.items?.[0]
  const [shouldFocusName, setShouldFocusName] = useState(false)

  const benchmark = program.data?.benchmarks?.find(
    b => b.benchmark === task.rule.params?.benchmarkName
  )

  const years: string[] = useMemo(
    () =>
      pipe(
        map((d: { year?: number }) => d.year),
        uniq,
        sort((a: number | undefined, b: number | undefined) => (a ?? 0) - (b ?? 0)),
        map(String)
      )(benchmark?.details ?? []) as string[],
    [benchmark?.details]
  )

  const allTraitIds: string[] = useMemo(
    () =>
      pipe(
        map((d: { traitId: string }) => d.traitId),
        uniq
      )(benchmark?.details ?? []) as string[],
    [benchmark?.details]
  )

  const createEntriesForTrait = useCallback(
    (traitId: string): PracticeTemplateDetail[] =>
      (benchmark?.details ?? [])
        .filter(d => d.traitId === traitId && d.year !== undefined)
        // we have filtered out all values that don't contain a year at this point
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map(d => ({ traitId, year: d.year!, value: undefined })),
    [benchmark?.details]
  )

  const {
    selectedId,
    draftName,
    setDraftName,
    draftDetails,
    setIsDirty,
    isDirty,
    ownerScope,
    nameError,
    setNameError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showUnsavedGuard,
    setShowUnsavedGuard,
    sidebarTemplates,
    isLoadingTemplates,
    selectedTemplate,
    canEdit,
    canDelete,
    availableTraitIds,
    isAdvisorOrAdmin,
    isDeleting,
    isSaving,
    authUserId,
    farmerUserId,
    requestNavigate,
    requestClose,
    handleUnsavedDiscard,
    handleValueSave,
    handleValueDelete,
    handleAddPractice,
    handlePrePopulate,
    handleSave,
    confirmName,
    handleOwnerScopeChange,
    handleDeleteConfirm,
  } = useTemplateEditorState({
    open,
    onClose,
    programId,
    taskId,
    initialTemplateId,
    allTraitIds,
    createEntriesForTrait,
  })

  const fieldHasData = useMemo(() => {
    if (!selectedField || !benchmark) return false
    return (benchmark.details ?? []).some(
      d => d.year && selectedField.findDetail({ traitId: d.traitId, year: d.year })
    )
  }, [selectedField, benchmark])

  const onPrePopulate = useCallback(() => {
    if (!selectedField || !benchmark) return
    const newDetails: PracticeTemplateDetail[] = (benchmark.details ?? [])
      .filter(d => d.year !== undefined)
      .map(d => {
        const fieldDetail = selectedField.findDetail({ traitId: d.traitId, year: d.year })
        if (!fieldDetail) return null
        const detailValueKey =
          task.rule.params?.traits?.find(t => t.traitId === d.traitId)?.detailValueKey ?? 'result'
        const detailValue = pickDetailValue(fieldDetail, detailValueKey)
        if (!Object.keys(detailValue).length) return null
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return { traitId: d.traitId, year: d.year!, ...detailValue }
      })
      .filter(Boolean) as PracticeTemplateDetail[]
    handlePrePopulate(newDetails)
  }, [selectedField, benchmark, task.rule.params?.traits, handlePrePopulate])

  const hasPractices = draftDetails.length > 0
  const hasName = !!draftName.trim()
  const canSave = isDirty && hasPractices && !isSaving && hasName

  const handleSaveAndClose = async () => {
    const success = await handleSave()
    if (success) {
      setShowUnsavedGuard(false)
      onClose()
    }
  }

  const handleNameAndSave = useCallback(() => {
    setShowUnsavedGuard(false)
    setShouldFocusName(true)
  }, [setShowUnsavedGuard])

  return (
    <TemplateEditorContext.Provider
      value={{ canSave, isSaving, hasName, onNameAndSave: handleNameAndSave }}
    >
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth="lg"
        fullWidth
        data-testid="template-editor-modal"
      >
        <Stack direction="row" sx={{ height: '80vh', overflow: 'hidden' }}>
          <TemplateEditorSidebar
            templates={sidebarTemplates}
            isLoading={isLoadingTemplates}
            selectedId={selectedId}
            onSelect={requestNavigate}
            draftName={draftName}
            onDraftNameChange={name => {
              setDraftName(name)
              setIsDirty(true)
              setNameError(null)
            }}
            nameError={nameError}
            canEdit={canEdit}
            onSave={handleSave}
            isAdvisorOrAdmin={isAdvisorOrAdmin}
            farmerUserId={farmerUserId}
            authUserId={authUserId}
          />
          <TemplateEditorMain
            task={task}
            draftDetails={draftDetails}
            years={years}
            onValueSave={handleValueSave}
            onValueDelete={handleValueDelete}
            onAddPractice={handleAddPractice}
            onPrePopulate={onPrePopulate}
            availableTraitIds={availableTraitIds}
            fieldHasData={fieldHasData}
            canEdit={canEdit}
            canChangeOwnerScope={isAdvisorOrAdmin}
            canDelete={canDelete}
            isDeleting={isDeleting}
            isSaving={isSaving}
            isDirty={isDirty}
            draftName={draftName}
            nameError={nameError}
            selectedId={selectedId}
            onDraftNameChange={name => {
              setDraftName(name)
              setIsDirty(true)
              setNameError(null)
            }}
            onConfirmName={confirmName}
            onDelete={() => setShowDeleteConfirm(true)}
            onClose={requestClose}
            onSave={handleSave}
            ownerScope={ownerScope}
            onOwnerScopeChange={handleOwnerScopeChange}
            shouldFocusName={shouldFocusName}
            onShouldFocusNameHandled={() => setShouldFocusName(false)}
          />
        </Stack>
      </Dialog>

      <TemplateDeleteConfirmDialog
        open={showDeleteConfirm}
        name={selectedTemplate?.name}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <TemplateUnsavedGuard
        open={showUnsavedGuard}
        selectedId={selectedId}
        onKeepEditing={() => setShowUnsavedGuard(false)}
        onDiscard={handleUnsavedDiscard}
        onSaveAndClose={handleSaveAndClose}
      />
    </TemplateEditorContext.Provider>
  )
}
